import { useState, useEffect } from 'react';
import ReactModal from 'react-modal';
import { useDispatch } from 'react-redux';

import verifyToken from 'services/store/features/roles/actions';
import styles from './Eligible.module.css';

import Slider from './Slider';
import Header from './Header/Header';
import Helper from './Helper';
import Footer from './Footer/Footer';
import Simulation from './Simulation/Simulation';

import type * as statuses from 'services/store/states';
import WaitingEligibleModal from './Modal/WaitingEligibleModal';
import LoginModal from './Modal/LoginModal';
import WaitingCreateModal from './Modal/WaitingCreateModal';
import ErrorCreateModal from './Modal/ErrorCreateModal';
import InvalidEligibleModal from './Modal/InvalidEligibleModal';
import type { AppDispatch } from 'services/store/types';
import type { ConfigState } from 'services/store/features/Config/types';

interface Props {
  status: typeof statuses[keyof typeof statuses];
  simulation: {
    amountSought: number;
    country: string;
    siren: string;
    typeInvestment: number | null;
    amountCA: number;
    amountRN: number;
  };
  user: {
    projectId: number;
    projectName: string;
    amountSought: number;
  };
  successEligibleTest?: boolean;
  invalidEligibleTest?: boolean;
  successCreateProject?: boolean;
  alreadyUsed: boolean;
  auth: boolean;
  config: ConfigState,
  actions: {
    EligibleTest: () => void;
    getSimulationData: () => void;
    postCreateProject: () => void;
  }
};

export default function Eligible({
  simulation, status, successEligibleTest, successCreateProject,
  alreadyUsed, auth, actions, user, invalidEligibleTest, config,
}: Props) {
  const urlModal = new URLSearchParams(window.location.search).get('modal')?.toLowerCase() || 'null';
  const [isOpen, setIsOpen] = useState(!Number.isNaN(parseInt(urlModal)));
  const [modalState, setModalState] = useState<number>(
    Number.isNaN(parseInt(urlModal)) === false ? parseInt(urlModal) : 0,
  );

  useEffect(() => {
    if (status === 'IDLE') actions.getSimulationData();
    else localStorage.setItem('eligible', JSON.stringify(simulation));
    if (invalidEligibleTest) setModalState(4);
    else if (successEligibleTest) setModalState(auth ? 2 : 1);
    if (successCreateProject) {
      localStorage.setItem('ayomi_project', JSON.stringify(user));
    }
  }, [successEligibleTest, successCreateProject, simulation, user, invalidEligibleTest]);

  const dispatch: AppDispatch = useDispatch();

  const params = new URLSearchParams(window.location.search);
  const refreshToken = params.get('refresh_token');
  const [firstLogin, setFirstLogin] = useState(true);

  useEffect(() => {
    if ((!auth && refreshToken) || (firstLogin && refreshToken)) {
      setFirstLogin(false);
      dispatch(verifyToken());
    }
  }, [auth]);

  const handleSubmit = () => {
    setIsOpen(true);
    actions.EligibleTest();
  };

  const closeModal = () => {
    setIsOpen(false);
    setModalState(0);
    dispatch({ type: 'RESET_CALL_ELIGIBLE' });
    dispatch({ type: 'RESET_CALL_CREATE_PROJECT' });
  };

  return (
    <section className={styles.content}>
      <Header />
      <div className={styles.backgroundWhite}>
        <div className={styles.simulationContainer}>
          <Simulation
            amountSought={simulation.amountSought}
            country={simulation.country}
            siren={simulation.siren}
            typeInvestment={simulation.typeInvestment}
            amountCA={simulation.amountCA}
            amountRN={simulation.amountRN}
            handleSubmit={handleSubmit}
          />
        </div>
        <div className={styles.mobile}>
          <Slider />
        </div>
        <Helper />
      </div>
      <Footer />
      <ReactModal
        isOpen={isOpen}
        className={`${styles.modal} ${[3, 4].includes(modalState) ? styles.largeModal : ''}`}
        overlayClassName={styles.modalOverlay}
        ariaHideApp={false}
      >
        {
          {
            0: <WaitingEligibleModal
              closeModal={closeModal}
              successEligibleTest={successEligibleTest}
              config={config}
            />,
            1: <LoginModal />,
            2: <WaitingCreateModal
              auth={auth}
              alreadyUsed={alreadyUsed}
              setModalState={setModalState}
              createProject={actions.postCreateProject}
              successCreateProject={successCreateProject}
              closeModal={closeModal}
              amountSought={user.amountSought}
            />,
            3: <ErrorCreateModal />,
            4: <InvalidEligibleModal closeModal={closeModal} config={config} />,
            default: <></>,
          }[modalState]
        }
      </ReactModal>
    </section>
  );
}
